/**
 * Single source of truth for resolving a four-corner identity to its
 * Pitch Glass colour.
 *
 * The colour VALUES live in tokens.css as `--c-tech / --c-phys /
 * --c-psych / --c-social` — the design-locked Eur OKLCH palette
 * (see docs/ground-up/04_visual_language_pitch_glass.md §2). This module
 * is the only place that maps the various corner-id schemes used across
 * the app onto those tokens, so every surface renders the SAME red /
 * green / amber / blue:
 *   - DB / shared:   `corner-technical | corner-physical | corner-psych | corner-social`
 *   - AI / prompts:  `technical_tactical | physical | psychological | social`
 *   - rating engine: `tech | phys | psych | social`
 *   - stripped form: `technical | physical | psych | social`
 *
 * Use `cornerCssVar()` for DOM inline styles + CSS — it returns
 * `var(--c-*)`, so the colour follows the active theme (daylight /
 * tactics-print). Use `resolveCornerColor()` for Canvas2D / WebGL, which
 * cannot read CSS custom properties — it reads the computed token at call
 * time (still theme-aware in the browser) and falls back to a hex
 * constant only when there is no DOM (SSR / unit tests).
 *
 * NOTE: this is a *corner-data* palette. Drill arrow colours
 * (`drawingTypes.ts`) and player kit colours (`playerRoles.ts`) are
 * separate, intentionally-fixed palettes and must NOT be routed through
 * here — a "pass" arrow or a "goalkeeper" bib does not change when the
 * brand accent switches green → blue → purple.
 */

export type CornerKey = 'tech' | 'phys' | 'psych' | 'social';

const ALIASES: Record<string, CornerKey> = {
  'corner-technical': 'tech',
  technical: 'tech',
  technical_tactical: 'tech',
  tech: 'tech',
  'corner-physical': 'phys',
  physical: 'phys',
  phys: 'phys',
  'corner-psych': 'psych',
  psychological: 'psych',
  psych: 'psych',
  'corner-social': 'social',
  social: 'social',
};

/**
 * No-DOM fallback only (SSR / tests). The canonical values are the
 * tokens.css OKLCH set; these hex are the documented spec equivalents,
 * close enough for the rare case where `getComputedStyle` is unavailable.
 */
const FALLBACK_HEX: Record<CornerKey, string> = {
  tech: '#C0392B',
  phys: '#27AE60',
  psych: '#F39C12',
  social: '#2980B9',
};

/** Normalise any corner-id scheme to the canonical key, or null. */
export function toCornerKey(id: string | null | undefined): CornerKey | null {
  if (!id) return null;
  const raw = id.toLowerCase();
  return ALIASES[raw] ?? ALIASES[raw.replace(/^corner-/, '')] ?? null;
}

/** CSS `var(--c-*)` reference for DOM styles. Unknown id → muted `--fg-3`. */
export function cornerCssVar(id: string | null | undefined): string {
  const key = toCornerKey(id);
  return key ? `var(--c-${key})` : 'var(--fg-3)';
}

/**
 * Concrete colour string for Canvas2D / WebGL. Reads the live token in
 * the browser (theme-aware); falls back to spec hex without a DOM.
 */
export function resolveCornerColor(id: string | null | undefined): string {
  const key = toCornerKey(id);
  if (!key) return '#9aa0a6';
  if (typeof window !== 'undefined' && typeof getComputedStyle === 'function') {
    const v = getComputedStyle(document.documentElement)
      .getPropertyValue(`--c-${key}`)
      .trim();
    if (v) return v;
  }
  return FALLBACK_HEX[key];
}
